import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Observable, forkJoin } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { MessageBoxButton, MessageBoxService, UID } from '@saliente/library';
import { ConfigService } from '../../general/config.service';
import { UploadService } from '../../general/upload.service';
import { cloneDeep } from 'src/app/library/utils';

@Component({
    selector: 'header-help-video-editor',
    templateUrl: 'header-help-video-editor.component.html',
    styleUrls: ['header-help-video-editor.component.scss'],
    standalone: false
})
export class HeaderHelpVideoEditorComponent {
	@Input() helpVideos: any[] = [];
	@Input() helpMenuId: string = '';
	@Output() cancel = new EventEmitter<void>();
	@Output() save = new EventEmitter<any[]>();

	public inEdit = false;
	public editingItem: any = null;
	public editingIndex: number = -1;

	public videoTypes = [
		{ text: 'YouTube', value: 'youtube' },
		{ text: 'Content', value: 'content' }
	];

	private pendingThumbnailFiles = new Map<any, File>();
	private pendingThumbnailFileEids = new Map<any, string>();

	constructor(private uploadService: UploadService, private configService: ConfigService, private messageBoxService: MessageBoxService) {}

	public startEdit(video: any): void {
		this.editingIndex = this.helpVideos.indexOf(video);
		this.editingItem = cloneDeep(video);
		this.editingItem.type = this.editingItem.type || 'youtube';
		this.inEdit = true;
	}

	public saveItemEdit(): void {
		if (this.editingIndex >= 0) {
			this.helpVideos[this.editingIndex] = this.editingItem;
		} else {
			this.helpVideos.push(this.editingItem);
		}
		this.inEdit = false;
	}

	public addItem(): void {
		this.editingIndex = -1;
		this.editingItem = { type: 'youtube' };
		this.inEdit = true;
	}

	public cancelItemEdit(): void {
		this.inEdit = false;
	}

	public deleteItem(index: number): void {
		const self = this;
		const item = self.helpVideos[index];
		self.messageBoxService.show({
			title: 'Ștergere tutorial',
			message: `Confirmi ștergerea tutorialului "${item.title}"?`,
			defaultButton: 'no',
			buttons: [MessageBoxButton.yes(), MessageBoxButton.no()],
			callback: function(result: string) {
				if (result === 'yes') {
					self.pendingThumbnailFiles.delete(item);
					self.pendingThumbnailFileEids.delete(item);
					self.helpVideos.splice(index, 1);
				}
			}
		});
	}

	public drop(event: CdkDragDrop<any[]>): void {
		moveItemInArray(this.helpVideos, event.previousIndex, event.currentIndex);
	}

	public moveUp(index: number): void {
		if (index > 0) {
			[this.helpVideos[index - 1], this.helpVideos[index]] =
			[this.helpVideos[index], this.helpVideos[index - 1]];
		}
	}

	public moveDown(index: number): void {
		if (index < this.helpVideos.length - 1) {
			[this.helpVideos[index], this.helpVideos[index + 1]] =
			[this.helpVideos[index + 1], this.helpVideos[index]];
		}
	}

	public onThumbnailSelect(event: Event, item: any): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) return;
		item.thumbnail = URL.createObjectURL(file);
		this.pendingThumbnailFiles.set(item, file);
		input.value = null;
	}

	private uploadThumbnail(item: any, file: File): Observable<any> {
		let documentEid = item.documentEid || this.pendingThumbnailFileEids.get(item);
		if (!documentEid) {
			documentEid = `${this.helpMenuId}_${UID()}`;
			item.documentEid = documentEid;
			this.pendingThumbnailFileEids.set(item, documentEid);
		}
		const meta = {
			category: "Public",
			documentEid: documentEid,
			fileName: file.name,
		};
		return this.configService.getDocumentsUploadUrl().pipe(
			take(1),
			switchMap(baseUrl =>
				this.uploadService.upload(baseUrl + '/files', file, meta).pipe(
					filter(data => !!data.done),
					take(1),
					map(data => {
						//item.thumbnail = `${baseUrl}/files/${data.fileName}`;
						item.thumbnail = `/documents/${documentEid}/thumbnail`
						return item;
					})
				)
			)
		);
	}

	public saveAll(): void {
		if (this.pendingThumbnailFiles.size === 0) {
			this.save.emit(this.helpVideos);
			return;
		}

		const observables: Observable<any>[] = [];
		this.pendingThumbnailFiles.forEach((file, item) => {
			observables.push(this.uploadThumbnail(item, file));
		});

		forkJoin(observables).subscribe(() => {
			this.pendingThumbnailFiles.clear();
			this.pendingThumbnailFileEids.clear();
			this.save.emit(this.helpVideos);
		});
	}
}
